import { jsx, jsxs } from "react/jsx-runtime";
import styled from "@mui/material/styles/styled";
import isArray from "lodash/isArray";
import isString from "lodash/isString";
import React from "react";
import { useParentBg } from "../../shared/hooks/current-color";
import { getMultilineEllipsisSX } from "../../shared/utils/styles";
import THEME, { BRAND_COLORS } from "../../theme";
import Box from "../Box";
import PlaceholderIcon, { EPlaceholderIconsNames } from "../PlaceholderIcon";
import Stack from "../Stack";
import Typography from "../Typography";
const BUTTON_SPACING = THEME.buttonGroup.spacing.m;
const {
  spacing: {
    contentHorizontalPadding: CONTENT_PADDING,
    subtitlePaddingBottom: SUBTITLE_PADDING,
    titlePaddingBottom: TITLE_PADDING,
    iconPaddingBottom: ICON_PADDING,
    paddingHMinPx: PADDING_H_MIN_PX = 24,
    paddingHMinPc: PADDING_H_MIN_PC = 15
  },
  minWidth: MIN_WIDTH = 200,
  maxWidth: MAX_WIDTH = 400,
  titleMaxLines: TITLE_MAX_LINES = 7,
  titleMaxLength: TITLE_MAX_LENGTH = 65,
  subtitleMaxLines: SUBTITLE_MAX_LINES = 5,
  subtitleMaxLength: SUBTITLE_MAX_LENGTH = 120
} = THEME.placeholder;
const PlaceholderWrapper = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  boxSizing: "border-box",
  margin: "auto auto"
}));
const PlaceholderContainer = styled(Box)(() => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  margin: `max(${PADDING_H_MIN_PX}px, ${PADDING_H_MIN_PC / 2}%)`,
  minWidth: `${MIN_WIDTH}px`,
  maxWidth: `${MAX_WIDTH}px`,
  boxSizing: "border-box"
}));
const PlaceholderItemSX = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  wordBreak: "break-word"
};
const PlaceholderIconWrapper = styled(Box)(() => ({
  ...PlaceholderItemSX,
  paddingBottom: ICON_PADDING
}));
const PlaceholderTitle = styled(Box)(({ color, theme }) => ({
  ...PlaceholderItemSX,
  color: theme.palette[color]?.contrast,
  paddingBottom: TITLE_PADDING,
  textAlign: "center"
}));
const PlaceholderSubTitle = styled(Box)(({ color, theme }) => ({
  ...PlaceholderItemSX,
  color: theme.palette[color]?.lowContrast,
  paddingBottom: SUBTITLE_PADDING,
  textAlign: "center"
}));
const TITLE_SX = getMultilineEllipsisSX(TITLE_MAX_LINES);
const SUBTITLE_SX = getMultilineEllipsisSX(SUBTITLE_MAX_LINES);
const CONTROLS_SX = {
  display: "flex",
  // flexWrap: 'wrap',
  justifyContent: "center",
  width: "100%",
  "& > button": {
    marginBottom: `${BUTTON_SPACING}px`,
    marginRight: `${BUTTON_SPACING / 2}px`,
    marginLeft: `${BUTTON_SPACING / 2}px`
  }
};
var EPlaceholderColors = /* @__PURE__ */ ((EPlaceholderColors2) => {
  EPlaceholderColors2["ERROR"] = "error";
  EPlaceholderColors2["INFO"] = "info";
  EPlaceholderColors2["SUCCESS"] = "success";
  EPlaceholderColors2["WARNING"] = "warning";
  EPlaceholderColors2["ELEVATION_0"] = "elevation0";
  EPlaceholderColors2["ELEVATION_1"] = "elevation1";
  EPlaceholderColors2["ELEVATION_2"] = "elevation2";
  EPlaceholderColors2["ELEVATION_3"] = "elevation3";
  EPlaceholderColors2["BRAND"] = "brand";
  EPlaceholderColors2["BLUE_ELEVATION_1"] = "blueElevation1";
  EPlaceholderColors2["BLUE_ELEVATION_2"] = "blueElevation2";
  return EPlaceholderColors2;
})(EPlaceholderColors || {});
const shorten = (value, l) => {
  if (!isString(value))
    return value;
  return value.length <= l ? value : value.substring(0, l).concat("...");
};
const Placeholder = (props) => {
  const {
    icon,
    title,
    desc,
    controls,
    color: rawColor,
    className
  } = props;
  const parentBg = useParentBg();
  const color = rawColor ?? parentBg;
  const onBrand = BRAND_COLORS.includes(color);
  const iconMode = onBrand ? "light" : void 0;
  return /* @__PURE__ */ jsx(PlaceholderWrapper, { className, children: /* @__PURE__ */ jsxs(PlaceholderContainer, { children: [
    icon && /* @__PURE__ */ jsx(PlaceholderIconWrapper, { children: /* @__PURE__ */ jsx(PlaceholderIcon, { name: icon, defaultMode: iconMode }) }),
    title && /* @__PURE__ */ jsx(PlaceholderTitle, { children: /* @__PURE__ */ jsx(Typography, { variant: "h5", color: "contrast", sx: TITLE_SX, children: shorten(title, TITLE_MAX_LENGTH) }) }),
    desc && /* @__PURE__ */ jsx(PlaceholderSubTitle, { color: "lowContrast", children: isArray(desc) ? /* @__PURE__ */ jsx(Stack, { children: desc.map((row) => /* @__PURE__ */ jsx(Typography, { variant: "body1", color: "lowContrast", sx: SUBTITLE_SX, children: shorten(row, SUBTITLE_MAX_LENGTH) })) }) : /* @__PURE__ */ jsx(Typography, { variant: "body1", color: "lowContrast", sx: SUBTITLE_SX, children: shorten(desc, SUBTITLE_MAX_LENGTH) }) }),
    /* @__PURE__ */ jsx(Box, { sx: CONTROLS_SX, children: React.Children.map(
      controls,
      (child) => child && React.cloneElement(child, { onBrand, color: child.props?.color ?? color })
    ) })
  ] }) });
};
var Placeholder_default = Placeholder;
export {
  EPlaceholderColors,
  EPlaceholderIconsNames,
  Placeholder_default as default
};
